import {
  useEffect,
  useState,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  dsrAuditsApi,
  DSRReport,
  AuditQueueRecord,
} from "../../../api/dsrAuditsApi";

export type DSRReportType = "genent" | "music-videos" | "sound-recordings";

export type AuditQueueColumnDef = {
  field?: string;
  header: string;
  body?: (row: any) => ReactNode;
  style?: CSSProperties;
};

export interface DSRReportsRouteShellProps {
  reportType: DSRReportType;
  title: string;
  description: string;
  auditQueueColumns: AuditQueueColumnDef[];
}

export const dsrMissingFieldsTemplate = (rowData: AuditQueueRecord) => {
  return rowData.missingFields?.length
    ? rowData.missingFields.join(", ")
    : "-";
};

const DSRReportsRouteShell = ({
  reportType,
  title,
  description,
  auditQueueColumns,
}: DSRReportsRouteShellProps) => {
  const [reports, setReports] = useState<DSRReport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [generating, setGenerating] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [auditQueueRecords, setAuditQueueRecords] = useState<
    AuditQueueRecord[]
  >([]);
  const [auditQueueLoading, setAuditQueueLoading] = useState<boolean>(false);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    fetchReports();
    fetchAuditQueue();
  }, [reportType]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await dsrAuditsApi.getReports(reportType);
      setReports(response || []);
    } catch (error) {
      console.error(`Error fetching ${reportType} reports:`, error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch reports",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditQueue = async () => {
    try {
      setAuditQueueLoading(true);
      const response = await dsrAuditsApi.getLatestAuditQueue(reportType);
      setAuditQueueRecords(response || []);
    } catch (error) {
      console.error(
        `Error fetching ${reportType} audit queue records:`,
        error
      );
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch audit queue records",
        life: 3000,
      });
    } finally {
      setAuditQueueLoading(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      await dsrAuditsApi.generateReport({ reportType });
      toast.current?.show({
        severity: "success",
        summary: "Report Generation Started",
        detail: "The report is being generated. Please refresh to see updates.",
        life: 5000,
      });
      setTimeout(() => {
        fetchReports();
        fetchAuditQueue();
      }, 2000);
    } catch (err: any) {
      if (err.response?.status === 409) {
        toast.current?.show({
          severity: "warn",
          summary: "Report Already Exists",
          detail:
            err.response.data?.message || "A report for today already exists.",
          life: 8000,
        });
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to generate report.",
          life: 8000,
        });
      }
    } finally {
      setGenerating(false);
    }
  };

  const deleteReport = async (reportId: string) => {
    setDeleting(reportId);
    confirmDialog({
      message: "Are you sure you want to delete this report?",
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await dsrAuditsApi.deleteReport(reportId);
          toast.current?.show({
            severity: "success",
            summary: "Report Deleted",
            detail: "Report deleted successfully.",
            life: 3000,
          });
          fetchReports();
        } catch (error) {
          console.error("Error deleting report:", error);
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to delete report.",
            life: 8000,
          });
        } finally {
          setDeleting(null);
        }
      },
      reject: () => {
        setDeleting(null);
      },
    });
  };

  const downloadReport = async (report: DSRReport) => {
    try {
      await dsrAuditsApi.downloadReport(report.id);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Report download started",
        life: 3000,
      });
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to download report",
        life: 3000,
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "available":
        return "pi pi-check-circle text-green-500";
      case "generating":
        return "pi pi-spin pi-spinner text-blue-500";
      case "failed":
        return "pi pi-times-circle text-red-500";
      default:
        return "pi pi-question-circle text-gray-500";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "available":
        return "Available";
      case "generating":
        return "Generating";
      case "failed":
        return "Failed";
      default:
        return "Unknown";
    }
  };

  const actionTemplate = (rowData: DSRReport) => {
    return (
      <div className="flex space-x-2">
        {rowData.status === "available" && (
          <Button
            icon="pi pi-download"
            className="p-button-sm p-button-success"
            onClick={() => downloadReport(rowData)}
            tooltip="Download Report"
          />
        )}
        {rowData.status === "failed" && (
          <Button
            icon="pi pi-refresh"
            className="p-button-sm p-button-warning"
            onClick={() => generateReport()}
            tooltip="Retry Generation"
          />
        )}
        <Button
          icon="pi pi-trash"
          className="p-button-sm p-button-danger"
          onClick={() => deleteReport(rowData.id)}
          tooltip="Delete Report"
        />
      </div>
    );
  };

  const statusTemplate = (rowData: DSRReport) => {
    return (
      <div className="flex items-center space-x-2">
        <i className={getStatusIcon(rowData.status)}></i>
        <span>{getStatusText(rowData.status)}</span>
      </div>
    );
  };

  const dateTemplate = (rowData: DSRReport) => {
    return new Date(rowData.createdAt).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toast ref={toast} />
      <ConfirmDialog
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        acceptClassName="p-button-danger"
        acceptLabel="Yes"
        rejectLabel="No"
      />

      {/* Header */}
      <div className="app-menubar-title rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-3">
          {/* Left: Title & Description (max 50%) */}
          <div className="md:max-w-lg md:pr-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{title}</h1>
            <p className="text-gray-600 text-lg break-normal whitespace-normal">
              {description}
            </p>
          </div>

          {/* Right: Stats stacked above Button */}
          <div className="flex flex-col items-center justify-center w-lg space-y-4 h-full ">
            {/* Stats */}
            <div className="flex flex-row justify-center items-center space-x-8 min-w-[220px]">
              <div className="text-center min-w-[60px]">
                <div className="text-xl font-bold text-blue-600">
                  {reports.length}
                </div>
                <div className="text-xs text-gray-600 font-medium">Total</div>
              </div>
              <div className="text-center min-w-[60px]">
                <div className="text-xl font-bold text-green-600">
                  {reports.filter((r) => r.status === "available").length}
                </div>
                <div className="text-xs text-gray-600 font-medium">
                  Available
                </div>
              </div>
              <div className="text-center min-w-[60px]">
                <div className="text-xl font-bold text-red-600">
                  {reports.filter((r) => r.status === "failed").length}
                </div>
                <div className="text-xs text-gray-600 font-medium">Failed</div>
              </div>
            </div>
            {/* Button */}
            <Button
              label="Generate New Report"
              icon="pi pi-refresh"
              severity="info"
              loading={generating}
              onClick={generateReport}
              className="px-4 py-2 text-base"
              style={{
                minWidth: 0,
                height: "44px",
                width: "100%",
                marginLeft: "10%",
              }}
            />
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <Card>
        <DataTable
          value={reports}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          className="p-datatable-sm"
          emptyMessage="No reports available"
        >
          <Column
            field="fileName"
            header="File Name"
            sortable
            style={{ minWidth: "200px" }}
          />
          <Column
            field="fileSize"
            header="File Size"
            sortable
            style={{ minWidth: "100px" }}
          />
          <Column
            field="recordCount"
            header="Records"
            sortable
            style={{ minWidth: "100px" }}
          />
          <Column
            field="createdAt"
            header="Generated"
            sortable
            body={dateTemplate}
            style={{ minWidth: "120px" }}
          />
          <Column
            field="status"
            header="Status"
            sortable
            body={statusTemplate}
            style={{ minWidth: "120px" }}
          />
          <Column
            header="Actions"
            body={actionTemplate}
            style={{ minWidth: "100px" }}
          />
        </DataTable>
      </Card>

      {/* Audit Queue Table */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Audit Queue (Latest Run)
          </h3>
        </div>
        {auditQueueLoading ? (
          <div className="flex justify-center items-center h-40">
            <ProgressSpinner />
          </div>
        ) : (
          <DataTable
            value={auditQueueRecords}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            className="p-datatable-sm"
            emptyMessage="No audit queue records for latest run"
          >
            {auditQueueColumns.map((col) => (
              <Column
                key={col.field ?? col.header}
                field={col.field}
                header={col.header}
                body={col.body}
                style={col.style}
              />
            ))}
          </DataTable>
        )}
      </Card>
    </div>
  );
};

export default DSRReportsRouteShell;
